<script setup lang="ts">
import { computed, ref } from 'vue'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { QuillEditor } from '@vueup/vue-quill'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'

import PageContent from '@/components/page-content.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import PageTitle from '@/components/page-title.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import Card from '@/components/Card/card-container.vue'
import CardContent from '@/components/Card/card-content.vue'
import CardHeader from '@/components/Card/card-header.vue'
import useLoader from '@/composables/loader'
import useNotifications from '@/composables/notifications'

import { useCreateCreditNote } from '@/composables/billing/credit-notes'
import {
  getAssetDetailsForCreditNote,
  getCustomerForCreditNote,
  getAssetVat
} from '@/api/billing/credit-notes'

import { faFloppyDisk, faXmark, faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import CreditNoteSkeleton from '@/features/credit-notes/components/CreditNoteSkeleton.vue'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import CreditNoteSkeletonCustomerDetails from '@/features/credit-notes/components/CreditNoteSkeletonCustomerDetails.vue'
import CreditNoteSkeletonBillTotal from '@/features/credit-notes/components/CreditNoteBillTotal.vue'
import CreditNoteSkeletonDetails from '@/features/credit-notes/components/CreditNoteSkeletonDetails.vue'
import CreditNoteLines from '@/features/credit-notes/components/CreditNoteLines.vue'
import { CreditNotesRouteNames } from '@/features/credit-notes/constants'
import { calcCreditNoteTotals } from '@/features/credit-notes/utils/totals'

import type { CreditNoteCharge } from '@/api/billing/credit-note-schemas'

defineOptions({ name: 'CreateCreditNote' })

const route = useRoute()
const router = useRouter()
const { addSuccess } = useNotifications()
const { wrap } = useLoader()

const assetCode = computed(() => String(route.query.assetCode || '').trim())
const hasAssetCode = computed(() => assetCode.value.length > 0)

const assetQuery = useQuery({
  queryKey: computed(() => ['credit-note', 'asset', assetCode.value]),
  enabled: hasAssetCode,
  queryFn: () => getAssetDetailsForCreditNote(assetCode.value),
  placeholderData: prev => prev
})

const customerQuery = useQuery({
  queryKey: computed(() => {
    const a = assetQuery.data.value
    return [
      'credit-note',
      'customer',
      a?.customerExternalIdentifier ?? null,
      a?.contract?.contractFundEntity?.id ?? null
    ]
  }),
  enabled: computed(() => !!assetQuery.data.value),
  queryFn: () => {
    const a = assetQuery.data.value!
    return getCustomerForCreditNote({
      customerExternalIdentifier: a.customerExternalIdentifier,
      contractFundEntityId: a.contract.contractFundEntity.id
    })
  },
  placeholderData: prev => prev
})

const vatQuery = useQuery({
  queryKey: computed(() => ['credit-note', 'vat', assetCode.value]),
  enabled: hasAssetCode,
  queryFn: () => getAssetVat(assetCode.value),
  placeholderData: prev => prev
})

const vatPercentage = computed(() => vatQuery.data.value?.ratePercentage ?? 0)

type Line = { id?: number; description: string; kWh: number; tariffPerKWh: number }
const lines = ref<Line[]>([{ description: '', kWh: 0, tariffPerKWh: 0 }])
const notes = ref('')

const canDeleteLine = computed(() => lines.value.length > 1)

const hasUniqueDescriptions = computed(() => {
  const seen = new Set<string>()
  for (const l of lines.value) {
    const desc = (l.description ?? '').trim()
    if (!desc) continue
    const key = desc.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
  }
  return true
})

const isFormValid = computed(() => {
  if (!lines.value.length) return false
  if (!hasUniqueDescriptions.value) return false
  return lines.value.every(l => (l.description ?? '').trim().length > 0)
})

const isReady = computed(
  () => !!assetQuery.data.value && !!customerQuery.data.value && vatQuery.isFetched.value
)

const totals = computed(() => calcCreditNoteTotals(lines.value, vatPercentage.value))
const displayTotalCents = computed(() => totals.value.totalCents)

const { mutateAsync: createCreditNote, isPending: isCreating } = useCreateCreditNote()

const canSave = computed(
  () => hasAssetCode.value && isReady.value && isFormValid.value && !isCreating.value
)

function goBackToDashboard() {
  router.push({ name: 'invoices.list' })
}

function addLine() {
  lines.value.push({ description: '', kWh: 0, tariffPerKWh: 0 })
}

async function removeLine(i: number) {
  await wrap(async () => {
    if (lines.value.length <= 1) return
    lines.value.splice(i, 1)
  })
}

function updateLine({ index, line }: { index: number; line: Line }) {
  lines.value[index] = line
}

async function doSave() {
  if (!canSave.value) return

  await wrap(async () => {
    const payload: CreditNoteCharge[] = lines.value.map(l => ({
      id: l.id,
      description: l.description,
      kWh: l.kWh,
      tariffPerKWh: l.tariffPerKWh
    }))

    const created = await createCreditNote({
      assetCode: assetCode.value,
      charges: payload,
      notes: notes.value
    })

    addSuccess({ title: 'Credit note created' })
    await router.replace({
      name: CreditNotesRouteNames.edit,
      params: { id: created.id },
      query: { assetCode: assetCode.value }
    })
  })
}

function doCancel() {
  router.push({ name: 'invoices.list' })
}

const isLoading = computed(
  () =>
    (!assetQuery.data.value && assetQuery.isFetching.value) ||
    (!customerQuery.data.value && customerQuery.isFetching.value) ||
    (!vatQuery.data.value && vatQuery.isFetching.value)
)

const isError = computed(
  () =>
    !hasAssetCode.value ||
    assetQuery.isError.value ||
    customerQuery.isError.value ||
    vatQuery.isError.value
)

const asset = computed(() => assetQuery.data.value)
const customer = computed(() => customerQuery.data.value)
const fund = computed(() => asset.value?.contract?.contractFundEntity)
</script>

<template>
  <PageContent>
    <ContentLoader :loading="isLoading" />
    <HeroSplashError v-if="isError && !asset" />

    <div v-else-if="asset && customer">
      <div class="mb-4">
        <PageTitle>
          {{ asset.projectName }}
          <template #subtitle
            >(Project: {{ asset.projectCode }} | Asset: {{ asset.assetCode }})</template
          >
        </PageTitle>

        <div class="mt-2">
          <ButtonSecondary
            :icon="faArrowLeft"
            :class="['one-h-width', $style['back-to-dashboard-btn']]"
            @click="goBackToDashboard">
            Back to Dashboard
          </ButtonSecondary>
        </div>
      </div>

      <CreditNoteSkeleton title="Credit Note">
        <template #credit-note-details>
          <CreditNoteSkeletonDetails :date="new Date()" date-format="d MMMM yyyy" />
        </template>

        <template #bill-total>
          <CreditNoteSkeletonBillTotal :amount="displayTotalCents" label="Your Credit" />
        </template>

        <template #owner-details>
          <InvoiceSkeletonOwnerDetails
            :logo-url="fund?.logoUrl ?? null"
            :name="fund?.name ?? ''"
            :address="fund?.address ?? ''"
            :vat-number="fund?.vatNumber ?? ''" />
        </template>

        <template #customer-details>
          <CreditNoteSkeletonCustomerDetails
            :name="customer.name"
            :address="[
              customer.addressLineOne ?? null,
              customer.addressLineTwo ?? null,
              customer.suburb ?? null,
              customer.city ?? null,
              customer.region ?? null,
              customer.country ?? null
            ]"
            :vat-number="customer.vatNumber ?? null" />
        </template>

        <template #line-items>
          <CreditNoteLines
            :lines="lines"
            :vat-percentage="vatPercentage"
            :can-delete="canDeleteLine"
            :readonly="false"
            @add="addLine"
            @remove="removeLine"
            @update:line="updateLine" />
        </template>

        <template #notes>
          <Card class="mt-4">
            <CardHeader uppercase>NOTES</CardHeader>
            <CardContent>
              <QuillEditor
                v-model:content="notes"
                contentType="html"
                theme="snow"
                placeholder="Please type notes here"
                style="min-height: 120px" />
            </CardContent>
          </Card>
        </template>
      </CreditNoteSkeleton>

      <div class="mt-4 d-flex flex-wrap align-items-center gap-3">
        <ButtonPrimary
          :icon="faFloppyDisk"
          class="color-temp-white"
          :disabled="!canSave"
          @click="doSave">
          Save
        </ButtonPrimary>

        <ButtonSecondary
          :icon="faXmark"
          class="one-h-width"
          :disabled="isCreating"
          @click="doCancel">
          Cancel
        </ButtonSecondary>
      </div>
    </div>
  </PageContent>
</template>

<style module>
.back-to-dashboard-btn {
  min-width: 180px;
}
</style>
